import { useMemo } from 'react';
import { FieldError } from '@common-fe/common-fe';
import _ from 'lodash';

import { REGEXPS, VALIDATORS } from '@/common';
import { useValidationUtils } from '@/utils/hooks/useValidationUtils';

import { useAccountFundingFields, useAccountFundingState } from '../../AccountFunding/hooks';
import { ROLLOVER_KEYS } from '../../AccountFunding/Rollover/Rollover.constants';
import { useCoverageFields, useCoverageState } from '../../Coverage/hooks';
import { usePlanDefinitionFields, usePlanDefinitionState } from '../../PlanDefinition/hooks';
import { PLAN_SETUP_FIELD, REQUIRED_TYPE } from '../../PlanSetup.constants';

import { filterEOYFields, shouldExcludeEOYFields } from './eoyFieldsUtils';

const VALIDATION_FIELDS = [
  PLAN_SETUP_FIELD.PLAN_NAME,
  PLAN_SETUP_FIELD.PLAN_TYPE,
  PLAN_SETUP_FIELD.PARENT_TEMPLATE,
  PLAN_SETUP_FIELD.PLAN_CODE,
  PLAN_SETUP_FIELD.PRIOR_PLAN_ID,
  PLAN_SETUP_FIELD.PLAN_YEAR,
  PLAN_SETUP_FIELD.AVAILABLE_BALANCE,
  PLAN_SETUP_FIELD.MAXIMUM_ELECTION_AMOUNT,
  PLAN_SETUP_FIELD.MINIMUM_ELECTION_AMOUNT,
  PLAN_SETUP_FIELD.CARD_OFFERED,
  // PLAN_SETUP_FIELD.PAYMENT_OPTIONS,
  PLAN_SETUP_FIELD.OMNIBUS,
  PLAN_SETUP_FIELD.ACCOUNT_CREATION,
  PLAN_SETUP_FIELD.CMS_REPORTING_REQUIRED,
  PLAN_SETUP_FIELD.ALLOW_INDIVIDUAL_CONTRIBUTIONS,
  PLAN_SETUP_FIELD.ALLOW_CONTRIBUTION_AFTER_ACCOUNT_CLOSURE,
  PLAN_SETUP_FIELD.FUNDING_METHOD,
  PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION,
  PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION_DELAY_DAYS,
  PLAN_SETUP_FIELD.EXTERNAL_BANK_ACCOUNT,
  PLAN_SETUP_FIELD.EXTERNAL_PUSH_RETURN_ACCOUNT,
    PLAN_SETUP_FIELD.BILLING_METHOD_TYPE_ID,
];

const TEMPLATE_VALIDATION_FIELDS = [
  PLAN_SETUP_FIELD.PLAN_NAME,
  PLAN_SETUP_FIELD.PLAN_TYPE,
  PLAN_SETUP_FIELD.AVAILABLE_BALANCE,
  PLAN_SETUP_FIELD.MAXIMUM_ELECTION_AMOUNT,
  PLAN_SETUP_FIELD.MINIMUM_ELECTION_AMOUNT,
  PLAN_SETUP_FIELD.CARD_OFFERED,
  // PLAN_SETUP_FIELD.PAYMENT_OPTIONS,
  PLAN_SETUP_FIELD.OMNIBUS,
  PLAN_SETUP_FIELD.ACCOUNT_CREATION,
  PLAN_SETUP_FIELD.CMS_REPORTING_REQUIRED,
  PLAN_SETUP_FIELD.ALLOW_INDIVIDUAL_CONTRIBUTIONS,
  PLAN_SETUP_FIELD.ALLOW_CONTRIBUTION_AFTER_ACCOUNT_CLOSURE,
  PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION,
  PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION_DELAY_DAYS,
  PLAN_SETUP_FIELD.EXTERNAL_BANK_ACCOUNT,
  PLAN_SETUP_FIELD.EXTERNAL_PUSH_RETURN_ACCOUNT,
];

const IGNORE_FIELDS: string[] = [
  // MAXIMUM_ELECTION_AMOUNT_KEY,
  // ROLLOVER_KEYS.MINIMUM_ROLLOVER_AMOUNT,
  // ROLLOVER_KEYS.MAXIMUM_ROLLOVER_AMOUNT,
  // 'minimumElectionAmount', 'cardOffered'];
];

const VALIDATION_FIELDS_MAP = {
  maximumElectionAmountCustom: PLAN_SETUP_FIELD.MAXIMUM_ELECTION_AMOUNT,
  maximumElectionAmountIRS: PLAN_SETUP_FIELD.MAXIMUM_ELECTION_AMOUNT,
  gracePeriodAmount: PLAN_SETUP_FIELD.GRACE_PERIOD,
  [ROLLOVER_KEYS.MAXIMUM_ROLLOVER_AMOUNT_CUSTOM]: ROLLOVER_KEYS.MAXIMUM_ROLLOVER_AMOUNT,
  [ROLLOVER_KEYS.MINIMUM_ROLLOVER_AMOUNT_CUSTOM]: ROLLOVER_KEYS.MINIMUM_ROLLOVER_AMOUNT,
  [ROLLOVER_KEYS.ROLLOVER_CLAIMS_YEARS_VALUE]: ROLLOVER_KEYS.ROLLOVER_CLAIMS,
};

const getFormatedString = (value: string) => _.capitalize(_.startCase(value));

export const usePlanActivateValidate = (isTemplateMode?: boolean) => {
  const { getValidateFields, getErrors } = useValidationUtils();

  const planDefinitionState = usePlanDefinitionState();
  const planDefinitionFields = usePlanDefinitionFields(true, isTemplateMode);
  const coverageState = useCoverageState();
  const coverageFields = useCoverageFields();

  const accountFundingState = useAccountFundingState();
  const accountFundingFields = useAccountFundingFields(true, false, isTemplateMode);

  const planDefinitionErrors: FieldError = useMemo(() => {
    const errors = getErrors(planDefinitionState, getValidateFields(planDefinitionFields));

    if (planDefinitionState?.fundIDFormula) {
      // @ts-ignore
      const fundIDFormula = planDefinitionState?.fundIDFormula?.replaceAll(REGEXPS.PARENTHESES, '');
      if (fundIDFormula) {
        try {
          VALIDATORS.ONLY_LATIN.validateSync(fundIDFormula);
        } catch {
          return {
            ...errors,
            fundIDFormula: {
              message: '',
              type: REQUIRED_TYPE,
            },
          };
        }
      }
    }

    return errors;
  }, [getErrors, getValidateFields, planDefinitionFields, planDefinitionState]);
  const coverageErrors: FieldError = useMemo(
    () => getErrors(coverageState, getValidateFields(coverageFields)),
    [coverageFields, coverageState, getErrors, getValidateFields]
  );
  const accountFundingErrors: FieldError = useMemo(
    () => getErrors(accountFundingState, getValidateFields(accountFundingFields)),
    [accountFundingFields, accountFundingState, getErrors, getValidateFields]
  );

  const filteredErrors = useMemo(() => {
    const allErrors: FieldError = {
      ...planDefinitionErrors,
      ...coverageErrors,
      ...accountFundingErrors,
    };
    const filtered: FieldError = {};
    _.forEach(allErrors, (item, key) => {
      if (IGNORE_FIELDS.includes(key)) {
        return;
      }
      if ((key === PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION || key === PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION_DELAY_DAYS) &&
          shouldExcludeEOYFields(accountFundingState?.fundingMethod, accountFundingState?.omnibus)) {
        return;
      }
      const newKey = _.get(VALIDATION_FIELDS_MAP, `[${key}]`) || key;
      filtered[newKey] = item;
    });
    return filtered;
  }, [accountFundingErrors, coverageErrors, planDefinitionErrors, accountFundingState?.fundingMethod, accountFundingState?.omnibus]);
  const errorsKeys = useMemo(() => _.map(filteredErrors, (item, key) => key), [filteredErrors]);
  const successes = useMemo(() => {
    const messages = isTemplateMode ? TEMPLATE_VALIDATION_FIELDS : VALIDATION_FIELDS;
    const filteredMessages = filterEOYFields(messages, accountFundingState?.fundingMethod, accountFundingState?.omnibus);
    return _.difference(filteredMessages, errorsKeys);
  }, [errorsKeys, isTemplateMode, accountFundingState?.fundingMethod, accountFundingState?.omnibus]);

  const errorMessages = useMemo(() => {
    const list = errorsKeys.map((item) => {
      if (item === PLAN_SETUP_FIELD.PLAN_NAME && isTemplateMode) {
        return getFormatedString(PLAN_SETUP_FIELD.PLAN_TEMPLATE_NAME);
      }
      if (item === PLAN_SETUP_FIELD.PARENT_TEMPLATE && !isTemplateMode) {
        return getFormatedString(PLAN_SETUP_FIELD.PLAN_TEMPLATE);
      }
      return getFormatedString(item);
    });

    return list;
  }, [errorsKeys, isTemplateMode]);
  const successMessages = useMemo(() => {
    const list = successes.map((item) => {
      if (item === PLAN_SETUP_FIELD.PLAN_NAME && isTemplateMode) {
        return getFormatedString(PLAN_SETUP_FIELD.PLAN_TEMPLATE_NAME);
      }
      if (item === PLAN_SETUP_FIELD.PARENT_TEMPLATE && !isTemplateMode) {
        return getFormatedString(PLAN_SETUP_FIELD.PLAN_TEMPLATE);
      }
      return getFormatedString(item);
    });

    return list;
  }, [isTemplateMode, successes]);

  const pendingMessages = useMemo(() => {
    const messages = isTemplateMode ? TEMPLATE_VALIDATION_FIELDS : VALIDATION_FIELDS;
    const filteredMessages = filterEOYFields(messages, accountFundingState?.fundingMethod, accountFundingState?.omnibus);
    const list = filteredMessages.map((item) => {
      if (item === PLAN_SETUP_FIELD.PLAN_NAME && isTemplateMode) {
        return getFormatedString(PLAN_SETUP_FIELD.PLAN_TEMPLATE_NAME);
      }
      if (item === PLAN_SETUP_FIELD.PARENT_TEMPLATE && !isTemplateMode) {
        return getFormatedString(PLAN_SETUP_FIELD.PLAN_TEMPLATE);
      }
      return getFormatedString(item);
    });
    return list;
  }, [isTemplateMode, accountFundingState?.fundingMethod, accountFundingState?.omnibus]);
  return {
    pendingMessages,
    errorMessages,
    successMessages,
  };
};
